import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideCheck,
  lucideMinus,
  lucidePlus,
  lucideSearch,
  lucideUndo2,
  lucideX,
} from '@ng-icons/lucide';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmInputDirective } from '@spartan-ng/helm/input';
import * as L from 'leaflet';
import { CampaignsService, PlaceResult } from '../../services/campaigns.service';

type Point = [number, number];

/** Must match AREA_IMPORT_MAX_KM2 in SolarInsight's address schema (summed over all areas). */
export const AREA_MAX_KM2 = 25;
const SEARCH_DEBOUNCE_MS = 300;
const DOUBLE_CLICK_MS = 400;

/** Approximate area of a small lat/lon polygon (shoelace on a local equirectangular projection). */
export function polygonAreaKm2(points: Point[]): number {
  if (points.length < 3) return 0;
  const lat0 = (points.reduce((sum, [lat]) => sum + lat, 0) / points.length) * (Math.PI / 180);
  const xy = points.map(([lat, lon]) => [lon * 111.32 * Math.cos(lat0), lat * 111.32]);
  const twice = xy.reduce((sum, [x1, y1], i) => {
    const [x2, y2] = xy[(i + 1) % xy.length];
    return sum + x1 * y2 - x2 * y1;
  }, 0);
  return Math.abs(twice) / 2;
}

export const totalAreaKm2 = (areas: Point[][]) =>
  areas.reduce((sum, area) => sum + polygonAreaKm2(area), 0);

/**
 * Outline one or more campaign areas on the map. Draw: click corners, close on
 * the first point. Edit: drag a corner, drag an edge's midpoint to add one,
 * double-click a corner to remove it, select an area to delete it. The model
 * holds only finished areas, so the wizard can restore them after "Zurück".
 */
@Component({
  selector: 'app-campaign-area-picker',
  imports: [NgIconComponent, HlmButtonDirective, HlmInputDirective],
  providers: [provideIcons({ lucideSearch, lucideUndo2, lucidePlus, lucideMinus, lucideCheck, lucideX })],
  templateUrl: './campaign-area-picker.html',
  styleUrl: './campaign-area-picker.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignAreaPicker implements AfterViewInit, OnDestroy {
  private readonly campaigns = inject(CampaignsService);
  readonly organizationId = input.required<string>();
  readonly polygons = model<Point[][]>([]);
  private readonly canvas = viewChild.required<ElementRef<HTMLDivElement>>('canvas');

  readonly areas = signal<Point[][]>([]);
  readonly draft = signal<Point[]>([]);
  readonly drawing = signal(true);
  readonly selected = signal<number | null>(null);
  readonly areaSizes = computed(() => this.areas().map(polygonAreaKm2));
  readonly totalKm2 = computed(() => totalAreaKm2(this.areas()));
  readonly tooLarge = computed(() => this.totalKm2() > AREA_MAX_KM2);
  readonly maxKm2 = AREA_MAX_KM2;

  readonly query = signal('');
  readonly results = signal<PlaceResult[]>([]);
  readonly searchError = signal<string | null>(null);

  private map?: L.Map;
  private readonly layer = L.layerGroup();
  private resize?: ResizeObserver;
  private searchTimer?: ReturnType<typeof setTimeout>;
  private searchRequest = 0;

  ngAfterViewInit(): void {
    // Inputs are bound by now; field initialisers would only see the default.
    this.areas.set(this.polygons().map((area) => [...area]));
    this.drawing.set(this.areas().length === 0);

    this.map = L.map(this.canvas().nativeElement, { zoomControl: false, doubleClickZoom: false, maxZoom: 19 })
      .setView([51.2, 10.3], 6);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.map);
    this.layer.addTo(this.map);
    this.map.on('click', (event: L.LeafletMouseEvent) => {
      if (!this.drawing()) {
        this.selected.set(null);
        this.redraw();
        return;
      }
      this.draft.update((points) => [...points, [event.latlng.lat, event.latlng.lng]]);
      this.redraw();
    });
    // The wizard dialog animates in, so Leaflet's first size reading is wrong.
    this.resize = new ResizeObserver(() => this.map?.invalidateSize());
    this.resize.observe(this.canvas().nativeElement);
    if (this.areas().length) this.fitAll();
    this.redraw();
  }

  ngOnDestroy(): void {
    clearTimeout(this.searchTimer);
    this.resize?.disconnect();
    this.map?.remove();
  }

  // ─── Toolbar ────────────────────────────────────────────────────────────────

  zoom(delta: number): void {
    this.map?.setZoom((this.map.getZoom() ?? 6) + delta);
  }

  startDrawing(): void {
    this.selected.set(null);
    this.drawing.set(true);
    this.redraw();
  }

  undoPoint(): void {
    this.draft.update((points) => points.slice(0, -1));
    this.redraw();
  }

  cancelDrawing(): void {
    this.draft.set([]);
    this.drawing.set(this.areas().length === 0);
    this.redraw();
  }

  finishArea(): void {
    if (this.draft().length < 3) return;
    this.areas.update((areas) => [...areas, this.draft()]);
    this.draft.set([]);
    this.drawing.set(false);
    this.selected.set(this.areas().length - 1);
    this.commit();
  }

  removeArea(index: number): void {
    this.areas.update((areas) => areas.filter((_, i) => i !== index));
    this.selected.set(null);
    if (!this.areas().length) this.drawing.set(true);
    this.commit();
  }

  focusArea(index: number): void {
    this.selected.set(index);
    this.map?.fitBounds(L.latLngBounds(this.areas()[index]), { padding: [32, 32], maxZoom: 17 });
    this.redraw();
  }

  onKeydown(event: KeyboardEvent): void {
    if ((event.target as HTMLElement).closest('input')) return;
    if (event.key === 'Escape' && this.draft().length) this.cancelDrawing();
    else if (event.key === 'Enter' && this.draft().length >= 3) this.finishArea();
    else if (event.key === 'Backspace' && this.draft().length) this.undoPoint();
    else if ((event.key === 'Delete' || event.key === 'Backspace') && this.selected() !== null) {
      this.removeArea(this.selected()!);
    } else return;
    event.preventDefault();
  }

  // ─── Place search ───────────────────────────────────────────────────────────

  onQuery(value: string): void {
    this.query.set(value);
    clearTimeout(this.searchTimer);
    const query = value.trim();
    if (query.length < 2) {
      this.results.set([]);
      return;
    }
    this.searchTimer = setTimeout(() => void this.search(query), SEARCH_DEBOUNCE_MS);
  }

  goTo(place: PlaceResult): void {
    this.results.set([]);
    this.query.set(place.label);
    if (place.bounds) {
      const [south, west, north, east] = place.bounds;
      this.map?.fitBounds([[south, west], [north, east]], { padding: [16, 16] });
    } else {
      this.map?.setView([place.latitude, place.longitude], 15);
    }
  }

  private async search(query: string): Promise<void> {
    const request = ++this.searchRequest;
    this.searchError.set(null);
    try {
      const results = await this.campaigns.searchPlaces(this.organizationId(), query);
      if (request === this.searchRequest) this.results.set(results);
    } catch {
      if (request === this.searchRequest) this.searchError.set('Ortssuche gerade nicht verfügbar.');
    }
  }

  // ─── Map rendering ──────────────────────────────────────────────────────────

  private commit(): void {
    this.polygons.set(this.areas());
    this.redraw();
  }

  private fitAll(): void {
    const points = this.areas().flat();
    if (points.length) this.map?.fitBounds(L.latLngBounds(points), { padding: [32, 32], maxZoom: 17 });
  }

  private color(name: '--primary' | '--danger'): string {
    // Leaflet writes SVG attributes, which cannot read CSS variables.
    return getComputedStyle(this.canvas().nativeElement).getPropertyValue(name).trim();
  }

  private redraw(): void {
    if (!this.map) return;
    this.layer.clearLayers();
    const color = this.color(this.tooLarge() ? '--danger' : '--primary');
    this.areas().forEach((area, index) => this.drawArea(area, index, color));
    this.drawDraft(color);
    this.canvas().nativeElement.classList.toggle('is-drawing', this.drawing());
  }

  private drawArea(area: Point[], index: number, color: string): void {
    const selected = this.selected() === index;
    const shape = L.polygon(area, {
      color,
      weight: selected ? 3 : 2,
      fillOpacity: selected ? 0.2 : 0.1,
      bubblingMouseEvents: false,
    }).addTo(this.layer);
    shape.on('click', () => {
      if (this.drawing()) return;
      this.selected.set(index);
      this.redraw();
    });

    const live = (points: Point[]) => shape.setLatLngs(points);
    const save = (points: Point[]) => {
      this.areas.update((areas) => areas.map((a, i) => (i === index ? points : a)));
      this.commit();
    };

    area.forEach((point, at) => {
      const corner = this.handle(point, 'area-vertex', 'Ecke ziehen · Doppel- oder Rechtsklick entfernt sie');
      corner.on('drag', () => live(area.map((p, i) => (i === at ? toPoint(corner) : p))));
      corner.on('dragend', () => save(area.map((p, i) => (i === at ? toPoint(corner) : p))));
      // Leaflet swallows dblclick on draggable markers, so detect it from clicks.
      let lastClick = 0;
      const remove = () => {
        if (area.length > 3) save(area.filter((_, i) => i !== at));
      };
      corner.on('click', () => {
        const now = Date.now();
        if (now - lastClick < DOUBLE_CLICK_MS) remove();
        lastClick = now;
      });
      corner.on('contextmenu', (event: L.LeafletMouseEvent) => {
        L.DomEvent.preventDefault(event.originalEvent);
        remove();
      });
    });

    area.forEach((point, at) => {
      const next = area[(at + 1) % area.length];
      const mid: Point = [(point[0] + next[0]) / 2, (point[1] + next[1]) / 2];
      const edge = this.handle(mid, 'area-midpoint', 'Ziehen, um eine Ecke einzufügen');
      const withInserted = (p: Point) => [...area.slice(0, at + 1), p, ...area.slice(at + 1)];
      edge.on('drag', () => live(withInserted(toPoint(edge))));
      edge.on('dragend', () => save(withInserted(toPoint(edge))));
    });
  }

  private drawDraft(color: string): void {
    const draft = this.draft();
    if (draft.length > 1) {
      L.polyline(draft, { color, weight: 2, dashArray: '6 6', interactive: false }).addTo(this.layer);
    }
    draft.forEach((point, index) => {
      const closes = index === 0 && draft.length >= 3;
      const marker = L.marker(point, {
        keyboard: false,
        icon: L.divIcon({
          className: closes ? 'area-vertex area-vertex-close' : 'area-vertex',
          iconSize: closes ? [18, 18] : [12, 12],
        }),
        title: closes ? 'Fläche schließen' : 'Eckpunkt',
        bubblingMouseEvents: false,
      }).addTo(this.layer);
      if (closes) marker.on('click', () => this.finishArea());
    });
  }

  private handle(point: Point, className: string, title: string): L.Marker {
    const size: L.PointExpression = className === 'area-vertex' ? [12, 12] : [10, 10];
    return L.marker(point, {
      draggable: !this.drawing(),
      keyboard: false,
      title,
      icon: L.divIcon({ className, iconSize: size }),
      bubblingMouseEvents: false,
      // Hidden while drawing a new area so it cannot be grabbed by accident.
      opacity: this.drawing() ? 0 : 1,
      interactive: !this.drawing(),
    }).addTo(this.layer);
  }
}

function toPoint(marker: L.Marker): Point {
  const { lat, lng } = marker.getLatLng();
  return [lat, lng];
}
